/**
 * Partner endpoints screen (epic 047 task 05; ADR-016 §7).
 *
 * The general transport editor for the partner-HTTP-call that is defined once,
 * on an endpoint row under the advertiser, and reused across campaign flights.
 * Operator profile: Postman-literate; can paste a body, replace values with
 * placeholders. Never asked to manage a SOAP envelope per flight.
 *
 * Screen → advertiser filter → list → create/edit dialog (side-dialog idiom,
 * matching partner-credentials screen). "Test with campaign …" runs the
 * epic-031 admin test run on a chosen campaign and navigates to its results.
 * Retire and delete confirm through the Dialog component, never window.confirm
 * (decision 12 of epic 006 — probes cannot dismiss native dialogs).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  AdvertiserResponse,
  CrmMethod,
  PartnerCredentialKind,
  PartnerCredentialRowResponse,
  PartnerEndpointResponse,
  PlaceholderSyntax,
} from '@leads-platform/shared';
import { api } from '../../lib/api';
import { joinPhrases, splitPhrases } from '../../lib/response-phrases';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Typeahead } from '../../components/typeahead';
import { StatusPill } from '../../components/ui/status-pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../components/ui/toast';
import { Shortcodes } from '../../components/formbits';
import { CRM_PLACEHOLDERS, PLACEHOLDER_SYNTAXES } from '../../lib/campaign-styles';
import type { CampaignDetailResponse } from '../../lib/api';
import {
  ApiError,
  type CreatePartnerEndpointWire,
  type UpdatePartnerEndpointWire,
  createPartnerEndpoint,
  createTestRun,
  deletePartnerEndpoint,
  getCampaignsForAdvertiser,
  getPartnerCredentials,
  getPartnerEndpoints,
  retirePartnerEndpoint,
  updatePartnerEndpoint,
} from './api';
// Partner-config check (085 D9): the server's dry run, surfaced before Save.
import { checkEndpointPartnerConfig } from '../partner-config-check/api';
import { PartnerConfigIssues } from '../partner-config-check/partner-config-issues';
import {
  hasNewBlocker,
  usePartnerConfigCheck,
} from '../partner-config-check/use-partner-config-check';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

interface HeaderRow {
  name: string;
  value: string;
}

interface VariableFormRow {
  key: string;
  label: string;
  required: boolean;
  default: string;
  hint: string;
}

interface BodyFieldRow {
  name: string;
  value: string;
}

interface EndpointFormState {
  name: string;
  method: CrmMethod;
  url: string; // stored in get_url or post_url depending on method
  headers: HeaderRow[];
  contentType: string; // one of KNOWN_CONTENT_TYPE_VALUES | 'custom' | ''
  bodyMode: 'template' | 'fields';
  bodyTemplate: string;
  bodyFields: BodyFieldRow[];
  placeholderSyntax: PlaceholderSyntax;
  credentialId: string; // '' = none
  duplicateContains: string;
  rejectedContains: string;
  acceptedContains: string;
  variables: VariableFormRow[];
}

const KNOWN_CONTENT_TYPE_VALUES = [
  'application/json',
  'application/x-www-form-urlencoded',
  'text/xml',
  'multipart/form-data',
] as const;

const CONTENT_TYPE_OPTIONS = [
  { value: '', label: 'None — omit Content-Type' },
  { value: 'application/json', label: 'application/json' },
  { value: 'application/x-www-form-urlencoded', label: 'application/x-www-form-urlencoded' },
  { value: 'text/xml', label: 'text/xml (SOAP)' },
  { value: 'multipart/form-data', label: 'multipart/form-data' },
  { value: 'custom', label: 'Custom — add Content-Type manually in headers list' },
] as const;

/** Body fields mode is only valid for form-encoded or multipart content. */
function supportsFieldsMode(contentType: string): boolean {
  return (
    contentType === 'application/x-www-form-urlencoded' ||
    contentType === 'multipart/form-data' ||
    contentType === 'custom' ||
    contentType === ''
  );
}

/**
 * Header rows after a content-type change. Selecting 'custom' scaffolds an
 * empty Content-Type row for the operator to fill in (B-05) — unless one is
 * already present, e.g. loaded from a stored endpoint or typed by hand. Any
 * other value strips user-added Content-Type rows so they don't conflict with
 * the managed select value.
 */
function headersForContentType(headers: HeaderRow[], contentType: string): HeaderRow[] {
  if (contentType === 'custom') {
    const present = headers.some((h) => h.name.toLowerCase() === 'content-type');
    return present ? headers : [...headers, { name: 'Content-Type', value: '' }];
  }
  return headers.filter((h) => h.name.toLowerCase() !== 'content-type');
}

/** A row action awaiting confirmation in the shared confirm Dialog. */
interface PendingConfirm {
  kind: 'retire' | 'delete';
  endpoint: PartnerEndpointResponse;
}

const initialForm: EndpointFormState = {
  name: '',
  method: 'POST',
  url: '',
  headers: [],
  contentType: '',
  bodyMode: 'template',
  bodyTemplate: '',
  bodyFields: [],
  placeholderSyntax: 'percent',
  credentialId: '',
  duplicateContains: '',
  rejectedContains: '',
  acceptedContains: '',
  variables: [],
};

// ---------------------------------------------------------------------------
// Helper: one error line from ApiError or generic Error
// ---------------------------------------------------------------------------

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fieldErrors = err.validationErrors
      ? Object.values(err.validationErrors).flat().join(' ')
      : '';
    return [err.message, fieldErrors].filter(Boolean).join(': ') || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Helper: narrow a <select> value to CrmMethod
// ---------------------------------------------------------------------------

function toCrmMethod(value: string): CrmMethod {
  return value === 'GET' ? 'GET' : 'POST';
}

// ---------------------------------------------------------------------------
// Helper: map endpoint response → form state
// ---------------------------------------------------------------------------

function endpointToForm(ep: PartnerEndpointResponse): EndpointFormState {
  const t = ep.transport;

  // Extract Content-Type from transport headers; keep remaining headers.
  const rawHeaders: Record<string, string> = t.headers ?? {};
  const headerRows: HeaderRow[] = [];
  let ctValue = '';

  for (const [key, value] of Object.entries(rawHeaders)) {
    if (key.toLowerCase() === 'content-type') {
      ctValue = value;
    } else {
      headerRows.push({ name: key, value });
    }
  }

  // Map the raw Content-Type value to a select option.
  let contentType: string;
  if (!ctValue) {
    contentType = '';
  } else if ((KNOWN_CONTENT_TYPE_VALUES as readonly string[]).includes(ctValue)) {
    contentType = ctValue;
  } else {
    // Non-standard Content-Type: route through 'custom' and keep it in headers.
    contentType = 'custom';
    headerRows.unshift({ name: 'Content-Type', value: ctValue });
  }

  const method = t.method ?? 'POST';
  const url = method === 'GET' ? (t.get_url ?? '') : (t.post_url ?? '');
  const bodyMode = t.body_fields !== undefined ? 'fields' : 'template';

  return {
    name: ep.name,
    method,
    url,
    headers: headerRows,
    contentType,
    bodyMode,
    bodyTemplate: t.body_template ?? '',
    bodyFields: t.body_fields?.map((f) => ({ name: f.name, value: f.value })) ?? [],
    placeholderSyntax: t.placeholder_syntax ?? 'percent',
    credentialId: ep.credentialId ?? '',
    duplicateContains: joinPhrases(t.response_rules?.duplicate_contains),
    rejectedContains: joinPhrases(t.response_rules?.rejected_contains),
    acceptedContains: joinPhrases(t.response_rules?.accepted_contains),
    variables: ep.variables.map((v) => ({
      key: v.key,
      label: v.label,
      required: v.required,
      default: v.default ?? '',
      hint: v.hint ?? '',
    })),
  };
}

// ---------------------------------------------------------------------------
// Helper: form state → transport + payload
// ---------------------------------------------------------------------------

function buildPayload(form: EndpointFormState, advertiserId: string): CreatePartnerEndpointWire {
  // Assemble headers: user rows + managed Content-Type from the select.
  const headersObj: Record<string, string> = {};
  for (const h of form.headers) {
    if (h.name.trim()) headersObj[h.name.trim()] = h.value;
  }
  if (form.contentType && form.contentType !== 'custom') {
    headersObj['Content-Type'] = form.contentType;
  }

  // Response rules — only set when at least one phrase is entered.
  const responsePhrases: {
    duplicate_contains?: string[];
    rejected_contains?: string[];
    accepted_contains?: string[];
  } = {};
  const duplicatePhrases = splitPhrases(form.duplicateContains);
  const rejectedPhrases = splitPhrases(form.rejectedContains);
  const acceptedPhrases = splitPhrases(form.acceptedContains);
  if (duplicatePhrases.length > 0) responsePhrases.duplicate_contains = duplicatePhrases;
  if (rejectedPhrases.length > 0) responsePhrases.rejected_contains = rejectedPhrases;
  if (acceptedPhrases.length > 0) responsePhrases.accepted_contains = acceptedPhrases;

  const transport = {
    method: form.method,
    ...(form.method === 'GET' ? { get_url: form.url } : { post_url: form.url }),
    ...(Object.keys(headersObj).length > 0 ? { headers: headersObj } : {}),
    placeholder_syntax: form.placeholderSyntax,
    // Body: template XOR fields (ADR-016 §5 — Zod refinement on the schema).
    ...(form.bodyMode === 'template' && form.bodyTemplate.trim()
      ? { body_template: form.bodyTemplate }
      : {}),
    ...(form.bodyMode === 'fields' && form.bodyFields.some((f) => f.name.trim())
      ? {
          body_fields: form.bodyFields
            .filter((f) => f.name.trim())
            .map((f) => ({ name: f.name, value: f.value })),
        }
      : {}),
    ...(Object.keys(responsePhrases).length > 0 ? { response_rules: responsePhrases } : {}),
  };

  // Variables: filter out blank-key rows; only include default/hint when non-empty.
  const variables = form.variables
    .filter((v) => v.key.trim())
    .map((v) => ({
      key: v.key,
      label: v.label,
      required: v.required,
      ...(v.default.trim() ? { default: v.default } : {}),
      ...(v.hint.trim() ? { hint: v.hint } : {}),
    }));

  return {
    advertiserId,
    name: form.name,
    transport,
    ...(form.credentialId ? { credentialId: form.credentialId } : {}),
    variables,
  };
}

// ---------------------------------------------------------------------------
// Client-side validation
// ---------------------------------------------------------------------------

/**
 * The credential placeholder spelling, per syntax (task 14, A11 —
 * `packages/api/src/modules/partners/placeholder.engine.ts:39`'s
 * `CREDENTIAL_PLACEHOLDER_NAMES` for `percent`, `:74–78`'s
 * `CREDENTIAL_BRACKET_TOKENS.credential` for `bracket`). Not importable here:
 * those constants live in `packages/api`, and admin-client depends only on
 * `@leads-platform/shared` (`package.json`). Two named copies, not a literal
 * scattered across the file — keyed by `PlaceholderSyntax` so every caller
 * reads the spelling for the endpoint's *own* `placeholderSyntax`, the same
 * dimension `substitutePlaceholders`/`templatePlacesCredential` switch on
 * (038-04 fix round: the guard was percent-only and wrongly rejected a valid
 * bracket-syntax endpoint — see `agents_output/038_04_2_reviewer.md`).
 */
const CREDENTIAL_PLACEHOLDER_TOKENS: Record<PlaceholderSyntax, string> = {
  percent: '%credential%',
  bracket: '[[CREDENTIAL-HERE]]',
};

/**
 * Whether the form's transport places its syntax's credential token
 * somewhere that actually travels for the form's method — mirrors
 * `request-builder.ts:253–273`'s `templatePlacesCredential` client-side: the
 * URL or any header **value** count for both methods; the body (template or
 * field values) counts only for POST, matching `buildPayload`'s active
 * `bodyMode` — a GET's body is never sent (`request-builder.ts:244–251`), and
 * only the active body mode reaches the wire. Which token spelling counts is
 * `form.placeholderSyntax`-dependent, exactly like the api's
 * `substitutePlaceholders(..., syntax, ...)` call.
 */
function formPlacesCredential(form: EndpointFormState): boolean {
  const token = CREDENTIAL_PLACEHOLDER_TOKENS[form.placeholderSyntax];
  if (form.url.includes(token)) {
    return true;
  }
  if (form.headers.some((h) => h.value.includes(token))) {
    return true;
  }
  if (form.method === 'POST') {
    if (form.bodyMode === 'template' && form.bodyTemplate.includes(token)) {
      return true;
    }
    if (form.bodyMode === 'fields' && form.bodyFields.some((f) => f.value.includes(token))) {
      return true;
    }
  }
  return false;
}

function validateForm(
  form: EndpointFormState,
  credentialKind: PartnerCredentialKind | null
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.name.trim()) {
    errors.name = 'Name is required';
  }
  if (!form.url.trim()) {
    errors.url = `${form.method === 'GET' ? 'GET' : 'POST'} URL is required`;
  }

  // Header names: no whitespace or CR/LF (ADR-016 §4).
  const badHeader = form.headers.findIndex((h) => h.name.trim() && /[\s\r\n]/.test(h.name));
  if (badHeader >= 0) {
    errors.headers = 'Header names must not contain whitespace or line breaks';
  }

  // Variable keys must be unique.
  const keys = form.variables.map((v) => v.key).filter((k) => k.trim());
  if (new Set(keys).size !== keys.length) {
    errors.variables = 'Variable keys must be unique';
  }

  // Variable labels are required — the shared schema rejects an empty label,
  // which otherwise only surfaces as an opaque 400 on save (B-02). Rows with
  // no key are dropped on save and stay exempt, matching buildPayload.
  form.variables.forEach((v, i) => {
    if (v.key.trim() && !v.label.trim()) {
      errors[`variableLabel-${i}`] = 'Variable label is required';
    }
  });

  // QA #81 (task 038-04): a header/query-kind credential can never be placed
  // in endpoint mode (`crm-config.view.ts:164–165` never builds `auth.header`
  // / `auth.param`) unless the template places the secret itself. Block the
  // save instead of letting it fail silently at send time
  // (`credential_config_invalid`, unreadable in the run detail before this
  // task).
  if ((credentialKind === 'header' || credentialKind === 'query') && !formPlacesCredential(form)) {
    const token = CREDENTIAL_PLACEHOLDER_TOKENS[form.placeholderSyntax];
    errors.credentialId =
      credentialKind === 'header'
        ? `This credential is header-kind: add ${token} as a request header value (or in the URL) so the endpoint knows where the token goes.`
        : `This credential is query-kind: add ${token} to the URL so the endpoint knows where the token goes.`;
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Screen component
// ---------------------------------------------------------------------------

/**
 * Partner endpoints (ADR-016 §7, epic 047 task 05) — the general transport
 * editor under the advertiser.
 */
export function PartnerEndpoints() {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [advertiserFilter, setAdvertiserFilter] = useState('');
  const [rows, setRows] = useState<PartnerEndpointResponse[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Guards against an earlier, slower request overwriting a later one's
  // result (e.g. switching the advertiser filter twice in quick succession) —
  // every call to `loadRows` claims the next sequence number, and a response
  // is applied only if it is still the most recent request in flight.
  const requestSeqRef = useRef(0);

  // Editor dialog state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EndpointFormState>(initialForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState<PartnerCredentialRowResponse[]>([]);
  // 085 D9: while the editor is open, the transport + variables it would save
  // are checked by the server's dry run. A failed check yields null (nothing shown).
  const builtForCheck = editorOpen ? buildPayload(form, advertiserFilter) : null;
  const partnerConfigIssues = usePartnerConfigCheck(
    builtForCheck
      ? {
          ...(editingId ? { endpointId: editingId } : {}),
          transport: builtForCheck.transport,
          variables: builtForCheck.variables,
        }
      : null,
    checkEndpointPartnerConfig
  );

  // Test-with-campaign dialog state.
  const [testEndpoint, setTestEndpoint] = useState<PartnerEndpointResponse | null>(null);
  const [testCampaigns, setTestCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [testCampaignId, setTestCampaignId] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testCampaignsLoading, setTestCampaignsLoading] = useState(false);

  // Confirm-dialog state for the row actions — one pending action renders a
  // single shared Dialog (B-03, B-04).
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Body template textarea ref for click-to-insert behaviour.
  const bodyTemplateRef = useRef<HTMLTextAreaElement>(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadRows = (advertiserId: string) => {
    // The list API takes an optional advertiserId (QA-13): an empty value
    // loads every advertiser's endpoints instead of a guaranteed 400 that
    // used to lock the whole screen behind the error banner (B-01's error is
    // still shown inline, not full-screen, on a genuine load failure).
    setListError(null);
    const seq = ++requestSeqRef.current;
    getPartnerEndpoints(advertiserId)
      .then((data) => {
        if (seq !== requestSeqRef.current) return; // a newer request has since started
        setRows(data);
      })
      .catch((err: unknown) => {
        if (seq !== requestSeqRef.current) return;
        setListError(describeError(err, 'Failed to load advertiser endpoints'));
      });
  };

  useEffect(() => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch(() => {
        // Best-effort: the filter just starts empty.
      });
    // Every endpoint loads on mount — the advertiser filter narrows it below
    // (QA-13: "view all advertiser endpoints", not pick-one-first).
    loadRows('');
  }, []);

  const refresh = () => loadRows(advertiserFilter);

  // ---------------------------------------------------------------------------
  // Editor open/close
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    if (advertiserFilter) {
      getPartnerCredentials(advertiserFilter)
        .then(setCredentials)
        .catch(() => setCredentials([]));
    }
    setEditingId(null);
    setForm({ ...initialForm });
    setFormErrors({});
    setSaveError(null);
    setEditorOpen(true);
  };

  const openEdit = (ep: PartnerEndpointResponse) => {
    if (advertiserFilter) {
      getPartnerCredentials(advertiserFilter)
        .then(setCredentials)
        .catch(() => setCredentials([]));
    }
    setEditingId(ep.id);
    setForm(endpointToForm(ep));
    setFormErrors({});
    setSaveError(null);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
  };

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  function setField<K extends keyof EndpointFormState>(key: K, value: EndpointFormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  }

  const handleContentTypeChange = (value: string) => {
    setForm((f) => ({
      ...f,
      contentType: value,
      headers: headersForContentType(f.headers, value),
      // Fields mode is only valid for urlencoded / multipart / custom / none.
      bodyMode: supportsFieldsMode(value) ? f.bodyMode : 'template',
    }));
  };

  // ---------------------------------------------------------------------------
  // Header rows helpers
  // ---------------------------------------------------------------------------

  const addHeader = () =>
    setForm((f) => ({ ...f, headers: [...f.headers, { name: '', value: '' }] }));

  const updateHeader = (i: number, field: keyof HeaderRow, value: string) => {
    setForm((f) => {
      const next = [...f.headers];
      next[i] = { ...next[i], [field]: value };
      return { ...f, headers: next };
    });
  };

  const removeHeader = (i: number) => {
    setForm((f) => ({ ...f, headers: f.headers.filter((_, idx) => idx !== i) }));
  };

  // ---------------------------------------------------------------------------
  // Body field rows helpers
  // ---------------------------------------------------------------------------

  const addBodyField = () =>
    setForm((f) => ({ ...f, bodyFields: [...f.bodyFields, { name: '', value: '' }] }));

  const updateBodyField = (i: number, field: keyof BodyFieldRow, value: string) => {
    setForm((f) => {
      const next = [...f.bodyFields];
      next[i] = { ...next[i], [field]: value };
      return { ...f, bodyFields: next };
    });
  };

  const removeBodyField = (i: number) => {
    setForm((f) => ({ ...f, bodyFields: f.bodyFields.filter((_, idx) => idx !== i) }));
  };

  // ---------------------------------------------------------------------------
  // Template chip insertion
  // ---------------------------------------------------------------------------

  const insertBodyChip = (code: string) => {
    const el = bodyTemplateRef.current;
    if (!el) {
      setForm((f) => ({ ...f, bodyTemplate: f.bodyTemplate + code }));
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newValue = el.value.slice(0, start) + code + el.value.slice(end);
    setForm((f) => ({ ...f, bodyTemplate: newValue }));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + code.length, start + code.length);
    }, 0);
  };

  // ---------------------------------------------------------------------------
  // Variable rows helpers
  // ---------------------------------------------------------------------------

  const addVariable = () =>
    setForm((f) => ({
      ...f,
      variables: [...f.variables, { key: '', label: '', required: false, default: '', hint: '' }],
    }));

  const updateVariable = (i: number, field: keyof VariableFormRow, value: string | boolean) => {
    setForm((f) => {
      const next = [...f.variables];
      next[i] = { ...next[i], [field]: value };
      return { ...f, variables: next };
    });
  };

  const removeVariable = (i: number) => {
    setForm((f) => ({ ...f, variables: f.variables.filter((_, idx) => idx !== i) }));
  };

  // ---------------------------------------------------------------------------
  // Save (create / update)
  // ---------------------------------------------------------------------------

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm(
      form,
      credentials.find((c) => c.id === form.credentialId)?.kind ?? null
    );
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // 085 D9 Save gate: the latest check holds a blocker this save would
    // create (not a grandfathered one) — stop before any request.
    if (hasNewBlocker(partnerConfigIssues)) {
      setSaveError('Fix the partner setup problem in Advertiser Send before saving');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      if (editingId === null) {
        // Create
        const payload = buildPayload(form, advertiserFilter);
        const ep = await createPartnerEndpoint(payload);
        success(`Endpoint "${ep.name}" created`);
      } else {
        // Update — same payload structure, minus advertiserId, as a partial.
        const base = buildPayload(form, advertiserFilter);
        const updateBody: UpdatePartnerEndpointWire = {
          name: base.name,
          transport: base.transport,
          // Always include credentialId: '' (the "None" option) is an explicit
          // clear the shared update schema preserves as null — distinct from an
          // absent field, which would leave the credential unchanged (F-6/B-06).
          credentialId: form.credentialId,
          variables: base.variables,
        };
        const ep = await updatePartnerEndpoint(editingId, updateBody);
        success(`Endpoint "${ep.name}" updated`);
      }
      closeEditor();
      refresh();
    } catch (err) {
      setSaveError(describeError(err, 'Failed to save the endpoint'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Retire / delete confirmation (B-03, B-04)
  // ---------------------------------------------------------------------------

  /** Row actions only open the confirm Dialog — window.confirm is banned (006-12). */
  const requestRetire = (ep: PartnerEndpointResponse) =>
    setPendingConfirm({ kind: 'retire', endpoint: ep });

  const requestDelete = (ep: PartnerEndpointResponse) =>
    setPendingConfirm({ kind: 'delete', endpoint: ep });

  const closeConfirm = () => setPendingConfirm(null);

  /** Fired by the Dialog's destructive button; the call happens only here. */
  const handleConfirm = async () => {
    if (pendingConfirm === null) return;
    const { kind, endpoint } = pendingConfirm;
    setConfirmBusy(true);
    try {
      if (kind === 'retire') {
        const updated = await retirePartnerEndpoint(endpoint.id);
        success(`Endpoint "${updated.name}" retired`);
      } else {
        await deletePartnerEndpoint(endpoint.id);
        success(`Endpoint "${endpoint.name}" deleted`);
      }
      closeConfirm();
      refresh();
    } catch (err) {
      // The dialog stays open so the action can be retried or cancelled.
      toastError(
        describeError(
          err,
          kind === 'retire' ? 'Failed to retire endpoint' : 'Failed to delete endpoint'
        )
      );
    } finally {
      setConfirmBusy(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Test with campaign
  // ---------------------------------------------------------------------------

  const openTestDialog = (ep: PartnerEndpointResponse) => {
    setTestEndpoint(ep);
    setTestCampaignId('');
    setTestError(null);
    setTestCampaigns([]);
    setTestCampaignsLoading(true);

    getCampaignsForAdvertiser(advertiserFilter || ep.advertiserId)
      .then((campaigns) => {
        // Filter client-side: campaigns whose crmConfig.endpoint_id matches.
        const matching = campaigns.filter(
          (c) => c.crmConfig?.endpoint_id !== undefined && String(c.crmConfig.endpoint_id) === ep.id
        );
        setTestCampaigns(matching);
      })
      .catch(() => setTestCampaigns([]))
      .finally(() => setTestCampaignsLoading(false));
  };

  const closeTestDialog = () => {
    setTestEndpoint(null);
    setTestCampaigns([]);
    setTestCampaignId('');
    setTestError(null);
  };

  const handleRunTest = async () => {
    if (!testCampaignId) return;
    setTestBusy(true);
    setTestError(null);
    try {
      await createTestRun(testCampaignId);
      closeTestDialog();
      navigate(`/test-runs/${testCampaignId}`);
    } catch (err) {
      setTestError(describeError(err, 'Failed to start the test run'));
    } finally {
      setTestBusy(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values for the editor
  // ---------------------------------------------------------------------------

  /** Shortcodes chips for the body template: lead placeholders + variable chips. */
  const templateChips = [
    ...(CRM_PLACEHOLDERS[form.placeholderSyntax] ?? CRM_PLACEHOLDERS.percent),
    ...form.variables.filter((v) => v.key.trim()).map((v) => `{{${v.key}}}`),
  ];

  const fieldsBodyDisabled = !supportsFieldsMode(form.contentType);

  /** The selected credential row — drives the placement hint and the save guard. */
  const selectedCredential = credentials.find((c) => c.id === form.credentialId);

  // Confirm-dialog payload — null-safe reads; the Dialog renders nothing while
  // closed, so these only surface with a pending action.
  const confirmKind = pendingConfirm?.kind;
  const confirmEndpointName = pendingConfirm?.endpoint.name ?? '';

  // ---------------------------------------------------------------------------
  // Derived values for the list (QA-13: view all + search)
  // ---------------------------------------------------------------------------

  /** The advertiser's name for a row's `advertiserId`, or the id itself if unresolved. */
  const advertiserName = (id: string): string => advertisers.find((a) => a.id === id)?.name ?? id;

  /** No advertiser picked → every row shows which advertiser it belongs to. */
  const showAdvertiserColumn = advertiserFilter === '';

  /**
   * Client-side, case-insensitive substring filter over the already-loaded
   * rows (out of scope: server-side search/pagination — dozens of rows) —
   * endpoint name, the row's resolved advertiser name, and either URL.
   */
  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((ep) => {
      const haystack = [
        ep.name,
        advertiserName(ep.advertiserId),
        ep.transport.get_url ?? '',
        ep.transport.post_url ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, searchQuery, advertisers]);

  const emptyMessage =
    rows.length === 0
      ? advertiserFilter
        ? 'No endpoints for this advertiser. Add one to define the call.'
        : 'No endpoints yet. Select an advertiser and add one to define the call.'
      : `No endpoints match "${searchQuery.trim()}".`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Advertiser Endpoints</h1>
          <p className="mt-2 text-muted-foreground">
            The advertiser HTTP call defined once per advertiser, reused across campaign flights.
          </p>
        </div>
        <Button onClick={openCreate} disabled={!advertiserFilter}>
          Add endpoint
        </Button>
      </div>

      {/* Advertiser filter + search */}
      <div className="mb-4 flex items-center gap-2">
        <Typeahead<AdvertiserResponse>
          aria-label="Filter by advertiser"
          className="w-64"
          options={advertisers}
          value={advertiserFilter || null}
          onChange={(v) => {
            const value = v ?? '';
            setAdvertiserFilter(value);
            loadRows(value);
          }}
          getId={(a) => a.id}
          getLabel={(a) => a.name}
          allOption={{ label: 'All advertisers' }}
        />
        <Input
          aria-label="Search endpoints"
          placeholder="Search by name, advertiser, or URL…"
          className="w-72"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* List-load error — inline so the advertiser filter stays usable and a
          failed load can be recovered from by picking another advertiser
          (B-01), instead of replacing the whole screen. */}
      {listError && (
        <div className="bg-destructive/10 text-destructive rounded-md p-4 mb-4">
          Error loading advertiser endpoints: {listError}
        </div>
      )}

      {/* Endpoint list */}
      <Card>
        <CardHeader>
          <CardTitle>Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {showAdvertiserColumn && <TableHead>Advertiser</TableHead>}
                <TableHead>Method / URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Credential</TableHead>
                <TableHead>Variables</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((ep) => {
                const t = ep.transport;
                const methodLabel = t.method ?? '—';
                const url = t.get_url ?? t.post_url ?? '—';
                return (
                  <TableRow key={ep.id}>
                    <TableCell className="font-medium">{ep.name}</TableCell>
                    {showAdvertiserColumn && (
                      <TableCell className="text-muted-foreground">
                        {advertiserName(ep.advertiserId)}
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {methodLabel} {url.length > 60 ? `${url.slice(0, 57)}…` : url}
                    </TableCell>
                    <TableCell>
                      <StatusPill value={ep.status === 'active' ? 'Active' : 'Retired'} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ep.credentialLabel
                        ? `${ep.credentialLabel}${ep.credentialHint ? ` (••••${ep.credentialHint})` : ''}`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ep.variables.length > 0
                        ? `${ep.variables.length} var${ep.variables.length !== 1 ? 's' : ''}`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(ep)}>
                        Edit
                      </Button>
                      {ep.status === 'active' && (
                        <Button variant="ghost" size="sm" onClick={() => requestRetire(ep)}>
                          Retire
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openTestDialog(ep)}>
                        Test with campaign…
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => requestDelete(ep)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredRows.length === 0 && !listError && (
                <TableRow>
                  <TableCell
                    colSpan={showAdvertiserColumn ? 7 : 6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Editor dialog                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId === null ? 'Add Endpoint' : 'Edit Endpoint'}</DialogTitle>
            <DialogDescription>
              Define the advertiser HTTP call. Campaigns point at this endpoint and supply the
              per-flight variable values.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave}>
            <div className="space-y-6 py-4">
              {saveError && <p className="text-sm text-destructive">{saveError}</p>}
              <PartnerConfigIssues issues={partnerConfigIssues} />

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="ep-name">Name</Label>
                <Input
                  id="ep-name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="e.g., Beacon Street — eletter external"
                />
                {formErrors.name && <p className="text-xs text-destructive">{formErrors.name}</p>}
              </div>

              {/* Method + URL */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ep-method">Method</Label>
                  <Select
                    id="ep-method"
                    value={form.method}
                    onChange={(e) => setField('method', toCrmMethod(e.target.value))}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="ep-url">{form.method} URL</Label>
                  <Input
                    id="ep-url"
                    mono
                    value={form.url}
                    onChange={(e) => setField('url', e.target.value)}
                    placeholder={
                      form.method === 'GET'
                        ? 'https://advertiser.example.com/in?email=%email%'
                        : 'https://advertiser.example.com/api/leads'
                    }
                  />
                  {formErrors.url && <p className="text-xs text-destructive">{formErrors.url}</p>}
                </div>
              </div>

              {/* Headers */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Request headers</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                    Add header
                  </Button>
                </div>
                {form.headers.map((h, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="w-2/5 shrink-0">
                      <Input
                        aria-label={`Header name ${i + 1}`}
                        mono
                        placeholder="X-Api-Key"
                        value={h.name}
                        onChange={(e) => updateHeader(i, 'name', e.target.value)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Input
                        aria-label={`Header value ${i + 1}`}
                        mono
                        placeholder="value or %credential%"
                        value={h.value}
                        onChange={(e) => updateHeader(i, 'value', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove header ${i + 1}`}
                      onClick={() => removeHeader(i)}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                {formErrors.headers && (
                  <p className="text-xs text-destructive">{formErrors.headers}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  For username + password (Basic) leave Authorization off — the credential's kind
                  places it automatically. Use <code>%credential%</code> in a header value to place
                  the token directly (e.g., Beacon Street's bare token header).
                </p>
              </div>

              {/* Content type */}
              <div className="space-y-2">
                <Label htmlFor="ep-content-type">Content type</Label>
                <Select
                  id="ep-content-type"
                  value={form.contentType}
                  onChange={(e) => handleContentTypeChange(e.target.value)}
                >
                  {CONTENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Body mode toggle + body area */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Body</Label>
                  {/* Two-button mode selector */}
                  <div className="flex rounded-md border border-input overflow-hidden">
                    <button
                      type="button"
                      aria-pressed={form.bodyMode === 'template'}
                      onClick={() => setField('bodyMode', 'template')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        form.bodyMode === 'template'
                          ? 'bg-accent text-white'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      Template
                    </button>
                    <button
                      type="button"
                      aria-pressed={form.bodyMode === 'fields'}
                      disabled={fieldsBodyDisabled}
                      onClick={() => !fieldsBodyDisabled && setField('bodyMode', 'fields')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        form.bodyMode === 'fields'
                          ? 'bg-accent text-white'
                          : fieldsBodyDisabled
                            ? 'bg-background text-muted-foreground opacity-40 cursor-not-allowed'
                            : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      Fields
                    </button>
                  </div>
                </div>

                {form.bodyMode === 'template' ? (
                  <div className="space-y-2">
                    <Textarea
                      ref={bodyTemplateRef}
                      mono
                      rows={6}
                      placeholder="Paste a body template here. Use placeholders to mark dynamic values."
                      value={form.bodyTemplate}
                      onChange={(e) => setField('bodyTemplate', e.target.value)}
                    />
                    <Shortcodes
                      codes={templateChips}
                      onInsert={insertBodyChip}
                      label="Placeholders — click to insert at cursor"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {form.bodyFields.map((bf, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="w-2/5 shrink-0">
                          <Input
                            aria-label={`Field name ${i + 1}`}
                            mono
                            placeholder="field_name"
                            value={bf.name}
                            onChange={(e) => updateBodyField(i, 'name', e.target.value)}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Input
                            aria-label={`Field value ${i + 1}`}
                            mono
                            placeholder="value or %email%"
                            value={bf.value}
                            onChange={(e) => updateBodyField(i, 'value', e.target.value)}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove field ${i + 1}`}
                          onClick={() => removeBodyField(i)}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addBodyField}>
                      Add field
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Fields mode — available for form-encoded and multipart bodies. The platform
                      generates the multipart boundary automatically.
                    </p>
                  </div>
                )}
              </div>

              {/* Placeholder syntax */}
              <div className="space-y-2">
                <Label htmlFor="ep-syntax">Placeholder syntax</Label>
                <Select
                  id="ep-syntax"
                  value={form.placeholderSyntax}
                  onChange={(e) => {
                    const v = e.target.value;
                    setField('placeholderSyntax', v === 'bracket' ? 'bracket' : 'percent');
                  }}
                >
                  {PLACEHOLDER_SYNTAXES.map((ps) => (
                    <option key={ps.key} value={ps.key}>
                      {ps.label}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  Per-advertiser, not global — Beacon Street uses the percent syntax; StockEarning
                  uses bracket.
                </p>
              </div>

              {/* Credential picker */}
              <div className="space-y-2">
                <Label htmlFor="ep-credential">Credential</Label>
                <Select
                  id="ep-credential"
                  value={form.credentialId}
                  onChange={(e) => setField('credentialId', e.target.value)}
                >
                  <option value="">None</option>
                  {credentials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                      {c.hint ? ` (••••${c.hint})` : ''}
                      {' — '}
                      {c.kind}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  The credential kind controls how the token is placed: bearer → Authorization:
                  Bearer; header → a custom header; query → a URL parameter; basic → the platform
                  base64-encodes user:password and sends Authorization: Basic. Select "None" if the
                  auth is managed via a header template above.
                </p>
                {selectedCredential && (
                  <p className="text-xs text-muted-foreground">
                    {selectedCredential.kind === 'bearer' || selectedCredential.kind === 'basic'
                      ? 'This credential is placed automatically as an Authorization header.'
                      : `This credential's kind (${selectedCredential.kind}) is not placed automatically — the transport must carry ${CREDENTIAL_PLACEHOLDER_TOKENS[form.placeholderSyntax]} where the advertiser expects it${selectedCredential.kind === 'query' ? ' (the URL)' : ' (a header value or the URL)'}.`}
                  </p>
                )}
                {formErrors.credentialId && (
                  <p className="text-xs text-destructive">{formErrors.credentialId}</p>
                )}
              </div>

              {/* Response phrases */}
              <div className="space-y-3">
                <Label>Response phrases</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="ep-duplicate" className="text-xs text-muted-foreground">
                      Duplicate
                    </Label>
                    <Input
                      id="ep-duplicate"
                      mono
                      placeholder="duplicate"
                      value={form.duplicateContains}
                      onChange={(e) => setField('duplicateContains', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ep-rejected" className="text-xs text-muted-foreground">
                      Rejected
                    </Label>
                    <Input
                      id="ep-rejected"
                      mono
                      placeholder=""
                      value={form.rejectedContains}
                      onChange={(e) => setField('rejectedContains', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ep-accepted" className="text-xs text-muted-foreground">
                      Accepted
                    </Label>
                    <Input
                      id="ep-accepted"
                      mono
                      placeholder=""
                      value={form.acceptedContains}
                      onChange={(e) => setField('acceptedContains', e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Comma-separated phrases, matched case-insensitively anywhere in the raw response
                  body. Duplicate defaults to "duplicate" when blank. A phrase can't contain a comma
                  — use a shorter part of it.
                </p>
              </div>

              {/* Variables */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Variables</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addVariable}>
                    Add variable
                  </Button>
                </div>
                <p className="text-xs font-medium text-amber-600 dark:text-amber-500">
                  Never type a token or password into a variable — credentials go to Advertiser
                  Credentials (ADR-016 §3).
                </p>
                {formErrors.variables && (
                  <p className="text-xs text-destructive">{formErrors.variables}</p>
                )}
                {form.variables.map((v, i) => (
                  <div key={i} className="rounded-md border border-input p-3 space-y-2">
                    <div className="flex gap-2">
                      <div className="w-1/3 space-y-1">
                        <Label className="text-xs text-muted-foreground">Key</Label>
                        <Input
                          aria-label={`Variable key ${i + 1}`}
                          mono
                          placeholder="source_date"
                          value={v.key}
                          onChange={(e) => updateVariable(i, 'key', e.target.value)}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs text-muted-foreground">Label</Label>
                        <Input
                          aria-label={`Variable label ${i + 1}`}
                          placeholder="Source date"
                          value={v.label}
                          onChange={(e) => updateVariable(i, 'label', e.target.value)}
                        />
                        {formErrors[`variableLabel-${i}`] && (
                          <p className="text-xs text-destructive">
                            {formErrors[`variableLabel-${i}`]}
                          </p>
                        )}
                      </div>
                      <div className="flex items-end pb-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove variable ${i + 1}`}
                          onClick={() => removeVariable(i)}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={v.required}
                          onChange={(e) => updateVariable(i, 'required', e.target.checked)}
                        />
                        Required
                      </label>
                      <div className="flex-1">
                        <Input
                          aria-label={`Variable default ${i + 1}`}
                          placeholder="Default value (optional)"
                          value={v.default}
                          onChange={(e) => updateVariable(i, 'default', e.target.value)}
                        />
                      </div>
                    </div>
                    <Input
                      aria-label={`Variable hint ${i + 1}`}
                      placeholder="Hint shown to the operator when filling in campaign values (optional)"
                      value={v.hint}
                      onChange={(e) => updateVariable(i, 'hint', e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editingId === null ? 'Create' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Test-with-campaign dialog                                            */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={testEndpoint !== null}
        onOpenChange={(open) => {
          if (!open) closeTestDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Test with campaign
              {testEndpoint ? ` — ${testEndpoint.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Pick a campaign that points at this endpoint. The platform will run an admin-mode test
              lead through it and show the raw advertiser response.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {testError && <p className="text-sm text-destructive">{testError}</p>}

            {testCampaignsLoading ? (
              <p className="text-sm text-muted-foreground">Loading campaigns…</p>
            ) : testCampaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No campaign uses this endpoint yet — point one at it in the campaign form and test
                from there.
              </p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="test-campaign">Campaign</Label>
                <Select
                  id="test-campaign"
                  value={testCampaignId}
                  onChange={(e) => setTestCampaignId(e.target.value)}
                >
                  <option value="" disabled>
                    Select a campaign…
                  </option>
                  {testCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} (#{c.id} · {c.status})
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeTestDialog}>
              Cancel
            </Button>
            {testCampaigns.length > 0 && (
              <Button type="button" disabled={!testCampaignId || testBusy} onClick={handleRunTest}>
                {testBusy ? 'Running…' : 'Run test'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Retire / delete confirm dialog (B-03, B-04)                          */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) closeConfirm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmKind === 'retire'
                ? `Retire "${confirmEndpointName}"?`
                : `Delete "${confirmEndpointName}"?`}
            </DialogTitle>
            <DialogDescription>
              {confirmKind === 'retire'
                ? 'Sends to this endpoint will record endpoint_unavailable. This cannot be undone from the admin — bringing the endpoint back means creating a new one and re-pointing every campaign that uses this one.'
                : 'This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeConfirm} disabled={confirmBusy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={confirmBusy}>
              {confirmKind === 'retire' ? 'Retire' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
